import { buildPageLayoutMetrics } from 'features/knowledgeDocumentViewer/components/viewer/continuous/pageLayoutMetrics'
import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

const SUPPRESS_DURATION_MS = 400
const SMOOTH_SCROLL_MAX_VIEWPORTS = 2
const SMOOTH_SCROLL_MAX_PAGES = 4
const PAGE_REPORT_DEBOUNCE_MS = 150

export function findPageAtScroll(
  scrollY: number,
  pageTops: number[],
  pageHeights: number[],
  pageGap: number
): number {
  let low = 0
  let high = pageTops.length - 1
  let bestPage = pageTops.length

  while (low <= high) {
    const mid = Math.floor((low + high) / 2)
    const page = mid + 1
    const pageEnd = pageTops[mid] + (pageHeights[mid] ?? 0) + pageGap / 2
    if (scrollY < pageEnd) {
      bestPage = page
      high = mid - 1
    } else {
      low = mid + 1
    }
  }

  return bestPage
}

function pageAtViewportCenter(
  container: HTMLDivElement,
  totalPages: number,
  pageTops: number[],
  pageHeights: number[],
  pageGap: number
): number {
  const viewportCenter = container.scrollTop + container.clientHeight / 2
  return Math.max(
    1,
    Math.min(
      totalPages,
      findPageAtScroll(viewportCenter, pageTops, pageHeights, pageGap)
    )
  )
}

export function useContinuousScrollSync(
  scrollContainerRef: RefObject<HTMLDivElement | null>,
  totalPages: number,
  getPageHeight: (page: number) => number,
  pageGap: number,
  initialPage: number,
  onPageChange: (page: number) => void,
  /** When true, skip the mount-time scroll-to-page (e.g. restoring a saved scroll). */
  suppressInitialScroll = false
) {
  const [currentPage, setCurrentPage] = useState(initialPage)
  const currentPageRef = useRef(initialPage)
  const [loadedRange, setLoadedRange] = useState({
    start: Math.max(1, initialPage - 2),
    end: Math.min(totalPages, initialPage + 4),
  })
  const isInitialScrollDone = useRef(suppressInitialScroll)
  const suppressUntil = useRef(0)
  const lastReportedPage = useRef(initialPage)
  const pendingReportPage = useRef<number | null>(null)
  const onPageChangeRef = useRef(onPageChange)
  onPageChangeRef.current = onPageChange

  const pageLayoutMetrics = useMemo(
    () => buildPageLayoutMetrics(totalPages, getPageHeight, pageGap),
    [totalPages, getPageHeight, pageGap]
  )
  const pageLayoutMetricsRef = useRef(pageLayoutMetrics)
  pageLayoutMetricsRef.current = pageLayoutMetrics
  const totalPagesRef = useRef(totalPages)
  totalPagesRef.current = totalPages
  const pageGapRef = useRef(pageGap)
  pageGapRef.current = pageGap

  const ready = getPageHeight(1) > 0

  const applyLocalPage = useCallback((page: number) => {
    currentPageRef.current = page
    setCurrentPage(page)
    const overscan = 3
    setLoadedRange({
      start: Math.max(1, page - overscan),
      end: Math.min(totalPagesRef.current, page + overscan),
    })
  }, [])

  const flushReportedPage = useCallback((page: number) => {
    pendingReportPage.current = null
    if (page === lastReportedPage.current) return
    lastReportedPage.current = page
    onPageChangeRef.current(page)
  }, [])

  const scrollToPage = useCallback(
    (page: number, smooth = true) => {
      const container = scrollContainerRef.current
      if (!container || !ready) return
      const metrics = pageLayoutMetricsRef.current
      const targetScrollTop = metrics.tops[page - 1] ?? 0
      const pageDistance = Math.abs(page - currentPageRef.current)
      const scrollDistance = Math.abs(targetScrollTop - container.scrollTop)
      const maxSmoothScrollDistance =
        container.clientHeight * SMOOTH_SCROLL_MAX_VIEWPORTS
      const shouldSmoothScroll =
        smooth &&
        pageDistance <= SMOOTH_SCROLL_MAX_PAGES &&
        scrollDistance <= maxSmoothScrollDistance

      suppressUntil.current =
        Date.now() + (shouldSmoothScroll ? SUPPRESS_DURATION_MS : 50)
      container.scrollTo({
        top: targetScrollTop,
        behavior: shouldSmoothScroll ? 'smooth' : 'instant',
      })
      applyLocalPage(page)
      // Programmatic navigation should update shared page state immediately.
      flushReportedPage(page)
    },
    [scrollContainerRef, ready, applyLocalPage, flushReportedPage]
  )

  // Stable scroll listener: page metrics / onPageChange are read from refs so
  // dimension loads (which rebuild layout metrics) don't tear down the listener
  // and cancel a pending debounced page report mid-scroll.
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container || !ready) return

    let rafId: number | null = null
    let debounceTimer: ReturnType<typeof setTimeout> | null = null

    const handleScroll = () => {
      if (rafId !== null) return
      rafId = requestAnimationFrame(() => {
        rafId = null

        if (Date.now() < suppressUntil.current) return

        const metrics = pageLayoutMetricsRef.current
        const centerPage = pageAtViewportCenter(
          container,
          totalPagesRef.current,
          metrics.tops,
          metrics.heights,
          pageGapRef.current
        )

        if (centerPage !== currentPageRef.current) {
          applyLocalPage(centerPage)
        }

        if (centerPage === lastReportedPage.current) {
          pendingReportPage.current = null
          if (debounceTimer) {
            clearTimeout(debounceTimer)
            debounceTimer = null
          }
          return
        }

        pendingReportPage.current = centerPage
        if (debounceTimer) clearTimeout(debounceTimer)
        debounceTimer = setTimeout(() => {
          debounceTimer = null
          const pageToReport = pendingReportPage.current
          if (pageToReport !== null) {
            flushReportedPage(pageToReport)
          }
        }, PAGE_REPORT_DEBOUNCE_MS)
      })
    }

    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      container.removeEventListener('scroll', handleScroll)
      if (rafId !== null) cancelAnimationFrame(rafId)
      if (debounceTimer) clearTimeout(debounceTimer)
      // If layout metrics remount this effect while a report was pending,
      // flush so continuous scroll doesn't leave history/toolbar stuck.
      if (pendingReportPage.current !== null) {
        flushReportedPage(pendingReportPage.current)
      }
    }
  }, [scrollContainerRef, ready, applyLocalPage, flushReportedPage])

  // When page heights settle (lazy image dims), recompute page-at-center even
  // if the user isn't actively scrolling.
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container || !ready) return
    if (Date.now() < suppressUntil.current) return

    const centerPage = pageAtViewportCenter(
      container,
      totalPages,
      pageLayoutMetrics.tops,
      pageLayoutMetrics.heights,
      pageGap
    )

    if (centerPage !== currentPageRef.current) {
      applyLocalPage(centerPage)
    }
    if (centerPage !== lastReportedPage.current) {
      flushReportedPage(centerPage)
    }
  }, [
    scrollContainerRef,
    ready,
    totalPages,
    pageLayoutMetrics,
    pageGap,
    applyLocalPage,
    flushReportedPage,
  ])

  useEffect(() => {
    if (suppressInitialScroll) {
      isInitialScrollDone.current = true
      return
    }
    if (isInitialScrollDone.current || !ready) return
    scrollToPage(initialPage, false)
    isInitialScrollDone.current = true
  }, [ready, initialPage, scrollToPage, suppressInitialScroll])

  const handleExternalPageChange = useCallback(
    (page: number) => {
      if (page === currentPageRef.current) return
      if (page === lastReportedPage.current) return
      scrollToPage(page)
    },
    [scrollToPage]
  )

  return { currentPage, loadedRange, handleExternalPageChange }
}
